import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import {
  StorageNativeStoreAction,
  TranslationStoreAction,
} from "src/components/shared-components/enum/enumStoreAction";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { TranslationService } from "src/service/translation.service";
import {
  translationLoadErrorAction,
  translationLoadErrorHandlingAction,
  translationLoadInitStartAction,
  translationLoadLastModifiedFromApiAction,
  translationLoadListFromApiAction,
  translationLoadSuccessAction,
  translationPopulateWithOldDataAction,
  translationUnsetKeyStorage,
} from "../actions/translation.actions";
import { currentUserData } from "../selectors/authentication.selectors";
import { listTranslation } from "../selectors/translation.selector";

/**
 * handle effects for translation
 */
@Injectable()
export class TranslationEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store,
    private _translationService: TranslationService
  ) { }

  /**
   * Effect who catch main event and unset specific server key storage
   */
  public translationUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      map((action) => translationUnsetKeyStorage())
    )
  );

  /**
   * Effect who force reload entire entity from API
   */
  public translationForceReloadEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TranslationStoreAction.TRANSLATION_FORCE_RELOAD),
      // map((action) => translationLoadListFromApiAction())
      map((action) => translationLoadInitStartAction())
    )
  );

  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public translationStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TranslationStoreAction.TRANSLATION_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listTranslation)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const translationObject: GenericStoreApiData = combinedObject[2];

        if (translationObject?.data || translationObject?.isOldDataInUse) {
          return translationLoadLastModifiedFromApiAction({
            translationObject: translationObject.data,
          });
        } else {
          return translationLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full list of translation effect
   */
  public translationListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TranslationStoreAction.TRANSLATION_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._translationService.getTranslation().pipe(
          map((translationObject: GenericApiData) =>
            translationLoadSuccessAction({ translationObject })
          ),
          catchError((error: HttpErrorResponse) =>
            of(translationLoadErrorHandlingAction(error))
          )
        );
      })
    )
  );

  /**
   * Load full list of translation by last modified effect
   */
  public translationListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TranslationStoreAction.TRANSLATION_LOAD_LAST_MODIFIED_FROM_API),
      switchMap((action: any) => {
        return this._translationService
          .getTranslationByLastModify(action.translationObject)
          .pipe(
            map((translationObject: GenericApiData) =>
              translationLoadSuccessAction({ translationObject })
            ),
            catchError((error: HttpErrorResponse) =>
              of(translationLoadErrorHandlingAction(error))
            )
          );
      })
    )
  );

  /**
   * Effect handling error in valid list loading
   */
  public translationListStartErrorHandlingEffects = createEffect(() =>
    this._actions$.pipe(
      ofType(TranslationStoreAction.TRANSLATION_LOAD_ERROR_HANDLING),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listTranslation)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      switchMap((action) => {
        const httpError: HttpErrorResponse = action[0] as HttpErrorResponse;
        const translationStore: GenericStoreApiData = action[2];

        if (
          translationStore?.loaded &&
          !translationStore.errorGeneric &&
          !translationStore.errorNotYetImplemented
        ) {
          return [
            translationPopulateWithOldDataAction({
              oldTranslationObject: translationStore.data,
            }),
          ];
        } else {
          return [translationLoadErrorAction()];
        }
      })
    )
  );
}
